/**
 * Matomo Analytics Integration
 * 
 * To use Matomo, you need to:
 * 1. Set up a Matomo instance (self-hosted or cloud)
 * 2. Get your Matomo URL and Site ID
 * 3. Set environment variables:
 *    - REACT_APP_MATOMO_URL: Your Matomo instance URL (e.g., https://analytics.example.com)
 *    - REACT_APP_MATOMO_SITE_ID: Your Matomo site ID (e.g., 1)
 * 
 * Or configure them directly in this file by replacing the process.env values below.
 */

// Matomo configuration
const MATOMO_URL = process.env.REACT_APP_MATOMO_URL || '';
const MATOMO_SITE_ID = process.env.REACT_APP_MATOMO_SITE_ID || '';

let matomoInitialized = false;

/**
 * Initialize Matomo tracking
 */
export const initMatomo = () => {
  // Skip if already initialized or if URL/Site ID not configured
  if (matomoInitialized || !MATOMO_URL || !MATOMO_SITE_ID) {
    if (!MATOMO_URL || !MATOMO_SITE_ID) {
      console.warn('Matomo analytics not configured. Set REACT_APP_MATOMO_URL and REACT_APP_MATOMO_SITE_ID environment variables.');
      console.warn('Current values:', { MATOMO_URL, MATOMO_SITE_ID });
    }
    return;
  }

  // Initialize Matomo tracker queue BEFORE loading script
  // This ensures commands are queued even if script loads slowly
  window._paq = window._paq || [];
  
  // Configure Matomo before loading the script
  window._paq.push(['setTrackerUrl', `${MATOMO_URL}/matomo.php`]);
  window._paq.push(['setSiteId', MATOMO_SITE_ID]);
  window._paq.push(['enableLinkTracking']);
  
  // Create Matomo tracking script
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.defer = true;
  script.src = `${MATOMO_URL}/matomo.js`;
  
  // Handle script load errors
  script.onerror = () => {
    console.error('Failed to load Matomo tracking script from:', script.src);
    console.error('Please check that your Matomo URL is correct and accessible.');
  };
  
  script.onload = () => {
    console.log('Matomo tracking script loaded successfully');
    // Track initial page view after script loads
    window._paq.push(['trackPageView']);
  };
  
  document.head.appendChild(script);

  matomoInitialized = true;
  console.log('Matomo analytics initialized with URL:', MATOMO_URL, 'Site ID:', MATOMO_SITE_ID);
};

/**
 * Track a page view
 * @param {string} customTitle - Optional custom page title
 * @param {string} customUrl - Optional custom URL
 */
export const trackPageView = (customTitle = null, customUrl = null) => {
  if (!window._paq || !matomoInitialized) {
    return;
  }

  if (customTitle || customUrl) {
    window._paq.push(['setCustomUrl', customUrl || window.location.pathname]);
    if (customTitle) {
      window._paq.push(['setDocumentTitle', customTitle]);
    }
  }
  window._paq.push(['trackPageView']);
};

/**
 * Track an event
 * @param {string} category - Event category
 * @param {string} action - Event action
 * @param {string} name - Optional event name
 * @param {number} value - Optional event value
 */
export const trackEvent = (category, action, name = null, value = null) => {
  if (!window._paq || !matomoInitialized) {
    return;
  }

  const eventParams = ['trackEvent', category, action];
  if (name) eventParams.push(name);
  if (value !== null) eventParams.push(value);
  
  window._paq.push(eventParams);
};

/**
 * Track a goal conversion
 * @param {number} goalId - Goal ID in Matomo
 * @param {number} revenue - Optional revenue value
 */
export const trackGoal = (goalId, revenue = null) => {
  if (!window._paq || !matomoInitialized) {
    return;
  }

  if (revenue !== null) {
    window._paq.push(['trackGoal', goalId, revenue]);
  } else {
    window._paq.push(['trackGoal', goalId]);
  }
};

/**
 * Set custom variables (user properties)
 * @param {number} index - Variable index (1-5)
 * @param {string} name - Variable name
 * @param {string} value - Variable value
 * @param {string} scope - Variable scope ('visit' or 'page')
 */
export const setCustomVariable = (index, name, value, scope = 'visit') => {
  if (!window._paq || !matomoInitialized) {
    return;
  }

  window._paq.push(['setCustomVariable', index, name, value, scope]);
};

/**
 * Set user ID for tracking
 * @param {string} userId - User ID
 */
export const setUserId = (userId) => {
  if (!window._paq || !matomoInitialized) {
    return;
  }

  window._paq.push(['setUserId', userId]);
};

/**
 * Reset user ID (e.g., on logout)
 */
export const resetUserId = () => {
  if (!window._paq || !matomoInitialized) {
    return;
  }

  window._paq.push(['resetUserId']);
};
